import React, { useState } from "react";
import { DateTime } from "luxon";
import {
    TListingValuationTabOutput,
    TValuationTabFact,
    TValuationTabStep,
    TValuationTabVersion,
} from "../../../../domain/codecs/formIO/ListingValuationTabFormIO";
import { penceToCopyText } from "../../../../shared/src/util";
import { THexColor } from "../../models/StringLiterals";
import { CRMCardOutsideLabelled } from "../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { CRMCollapsibleSectionComponent } from "../CRMCollapsibleSectionComponent/CRMCollapsibleSectionComponent";
import { CRMTableHeaderRow } from "../Simple/CRMTableHeaderRow/CRMTableHeaderRow";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { Padding } from "../BuildingBlocks/Padding";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMButtonTertiary } from "../CRMButtonTertiary/CRMButtonTertiary";
import { editableFact, STEP_LABELS, TOnValuationAction } from "./valuationTabSteps";

const FACT_COLS = "1fr 130px 90px 80px 90px";

const USED_LABELS: Record<TValuationTabFact["used"], string> = {
    ours: "Ours",
    declared: "Vendor said",
    predicted: "Estimated",
    nothing: "no figure either side",
};

const blank = (value: string | null): string => value ?? "—";

const FactRow = (props: {
    fact: TValuationTabFact;
    isSubmitting: boolean;
    onAction: TOnValuationAction;
}): JSX.Element => {
    const editable = editableFact(props.fact.field);

    return (
        <SpacingRow spacing={CRMSpacing.SMALL} childSize={FACT_COLS} alignItems="center">
            <SpacingColumn spacing={CRMSpacing.TINY}>
                <Text size={CRMFontSizes.X_SMALL}>{props.fact.field}</Text>
                {/* Naming the winner per row is the point. Testers could not answer "is the
                    price built on 92m² or 88m²?" from a rule stated at the top of the card. */}
                <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                    {props.fact.used === "nothing"
                        ? USED_LABELS.nothing
                        : `price used: ${USED_LABELS[props.fact.used].toLowerCase()}`
                    }
                </Text>
            </SpacingColumn>
            <Text size={CRMFontSizes.X_SMALL}>{blank(props.fact.ours)}</Text>
            <Text size={CRMFontSizes.X_SMALL}>{blank(props.fact.provider_declared)}</Text>
            <Text size={CRMFontSizes.X_SMALL}>{blank(props.fact.provider_predicted)}</Text>
            {/* Our own figure is edited in the specifics form on the left, like every
                other listing field. What only this card can offer is the supplier's
                value — taking it is a command, not an edit: it copies their figure into
                our column and requeues the price, because the number on the card was
                worked out from the value just replaced. */}
            {editable !== null && props.fact.provider_declared !== null && props.fact.conflicts
                ? <CRMButtonTertiary
                    label="Take theirs"
                    fullWidth={false}
                    disabled={props.isSubmitting}
                    onClick={() => props.onAction("adopt_fact", { fact_field: editable })}
                />
                : <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>—</Text>
            }
        </SpacingRow>
    );
};

const DOT_COLOURS: Record<TValuationTabStep["status"], THexColor> = {
    succeeded: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0,
    pending: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_0,
    failed: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0,
    skipped: CRMColors.NEUTRAL_8,
};

const StepRow = (props: { step: TValuationTabStep }): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.SMALL} childSize="auto 1fr auto" alignItems="center">
        <Background color={DOT_COLOURS[props.step.status]} width="8px" height="8px" borderRadius="50%" />
        <Text size={CRMFontSizes.X_SMALL}>{STEP_LABELS[props.step.step]}</Text>
        <Text size={CRMFontSizes.X_SMALL} align="right" color={CRMColors.NEUTRAL_4}>
            {props.step.status === "failed" && props.step.last_error !== null
                ? props.step.last_error
                : props.step.status
            }
        </Text>
    </SpacingRow>
);

const VersionRow = (props: { version: TValuationTabVersion; versionNumber: number }): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.SMALL} childSize="1fr auto" alignItems="center">
        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
            {`#${props.versionNumber} · ${DateTime.fromISO(props.version.created_at).setZone("Europe/London").toFormat("d MMM yyyy")}`}
        </Text>
        <Text size={CRMFontSizes.X_SMALL} align="right">
            {props.version.recommended_value_pence === null
                ? "no price"
                : penceToCopyText(props.version.recommended_value_pence)
            }
        </Text>
    </SpacingRow>
);

export const CRMValuationDetailsCard = (props: {
    output: TListingValuationTabOutput;
    isSubmitting: boolean;
    onAction: TOnValuationAction;
}): JSX.Element => {
    const { output } = props;
    const [openSection, setOpenSection] = useState<"facts" | "data" | "history" | "none">("none");

    const toggle = (section: "facts" | "data" | "history") =>
        setOpenSection(openSection === section ? "none" : section);

    const conflictCount = output.facts.filter((fact) => fact.conflicts).length;

    return (
        <CRMCardOutsideLabelled labelColor="neutral-8">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.TINY}>
                    <CRMCollapsibleSectionComponent
                        title={conflictCount === 0
                            ? "Property facts"
                            : `Property facts (${conflictCount} differ)`
                        }
                        isOpen={openSection === "facts"}
                        onOpenChange={() => toggle("facts")}
                    >
                        <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                            {/* The table shows every fact now, in dispute or not. It used
                                to render only disagreements, so a property the suppliers
                                know nothing about got a reassuring sentence and no rows —
                                and no way to describe the property at all. */}
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                {output.provider_facts_updated_at === null &&
                                    <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                        No supplier facts yet — fill in what you know and
                                        the price is worked out from that.
                                    </Text>
                                }
                                <CRMTableHeaderRow
                                    spacing={CRMSpacing.SMALL}
                                    spacingRowChildSize={FACT_COLS}
                                    headers={["Fact", "Ours", "Vendor said", "Estimated", ""]}
                                />
                                {output.facts.map((fact) =>
                                    <FactRow
                                        // The stored value is part of the key so the row
                                        // remounts when it changes, discarding whatever
                                        // is half-typed in the box. Without it, taking
                                        // the supplier's figure leaves the old number in
                                        // the input and the row immediately offers to
                                        // "Save" it back over the value just adopted.
                                        key={`${fact.field}:${fact.ours ?? ""}`}
                                        fact={fact}
                                        isSubmitting={props.isSubmitting}
                                        onAction={props.onAction}
                                    />,
                                )}
                            </SpacingColumn>
                        </Padding>
                    </CRMCollapsibleSectionComponent>

                    <CRMCollapsibleSectionComponent
                        title="Where the data came from"
                        isOpen={openSection === "data"}
                        onOpenChange={() => toggle("data")}
                    >
                        <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                            <SpacingColumn spacing={CRMSpacing.SMALL}>
                                {output.steps.length === 0
                                    ? <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                        Nothing has been collected for this property yet.
                                    </Text>
                                    : output.steps.map((step) => <StepRow key={step.step} step={step} />)
                                }
                                {output.provider_facts_updated_at !== null &&
                                    <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                        {`Property facts last updated ${DateTime.fromISO(output.provider_facts_updated_at).setZone("Europe/London").toFormat("d MMM 'at' HH:mm")}`}
                                    </Text>
                                }
                            </SpacingColumn>
                        </Padding>
                    </CRMCollapsibleSectionComponent>

                    {output.versions.length > 1 &&
                        <CRMCollapsibleSectionComponent
                            title="Earlier prices"
                            isOpen={openSection === "history"}
                            onOpenChange={() => toggle("history")}
                        >
                            <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                <SpacingColumn spacing={CRMSpacing.TINY}>
                                    {output.versions.map((version, index) =>
                                        <VersionRow
                                            key={version.version_id}
                                            version={version}
                                            versionNumber={output.versions.length - index}
                                        />,
                                    )}
                                </SpacingColumn>
                            </Padding>
                        </CRMCollapsibleSectionComponent>
                    }
                </SpacingColumn>
            </Padding>
        </CRMCardOutsideLabelled>
    );
};
